export interface BuildingOption {
  id: string;
  name?: string;
}

interface UserWithBuilding {
  buildingId?: string;
  role?: string;
  id?: string;
}

const STORAGE_PREFIX = 'ebms.selectedBuildingId';
const memorySelectionByUser = new Map<string, string>();

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function getPersistedBuildingId(userId?: string): string {
  if (!userId) {
    return '';
  }
  const cached = memorySelectionByUser.get(userId);
  if (cached) {
    return cached;
  }
  try {
    const stored = localStorage.getItem(storageKey(userId)) || '';
    if (stored) {
      memorySelectionByUser.set(userId, stored);
    }
    return stored;
  } catch {
    return '';
  }
}

export function persistSelectedBuilding(userId: string | undefined, buildingId: string): void {
  if (!userId) {
    return;
  }
  if (!buildingId) {
    memorySelectionByUser.delete(userId);
    try {
      localStorage.removeItem(storageKey(userId));
    } catch {
      /* ignore */
    }
    return;
  }
  memorySelectionByUser.set(userId, buildingId);
  try {
    localStorage.setItem(storageKey(userId), buildingId);
  } catch {
    /* ignore */
  }
}

export function clearPersistedBuildingSelection(userId?: string): void {
  if (!userId) {
    return;
  }
  memorySelectionByUser.delete(userId);
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    /* ignore */
  }
}

export function selectBuilding(
  userId: string | undefined,
  buildingId: string,
  setSelectedBuildingId: (id: string) => void,
): void {
  persistSelectedBuilding(userId, buildingId);
  setSelectedBuildingId(buildingId);
}

/**
 * Returns the building id to auto-select when the user should not pick manually.
 * - Single assigned building: always auto-select.
 * - Operator/resident with buildingId in the list: auto-select that building.
 */
export function resolveDefaultBuildingId(
  buildings: BuildingOption[],
  user?: UserWithBuilding | null,
): string {
  if (buildings.length === 1) {
    return buildings[0].id;
  }

  if (
    user?.buildingId &&
    buildings.some((building) => building.id === user.buildingId)
  ) {
    return user.buildingId;
  }

  return buildings[0]?.id ?? '';
}

/** True when the building dropdown should be shown (at least one assigned building). */
export function shouldShowBuildingSelector(buildings: BuildingOption[]): boolean {
  return buildings.length > 0;
}

/** True when the user can change the building in the dropdown. */
export function needsBuildingSelection(buildings: BuildingOption[]): boolean {
  return buildings.length > 1;
}

/** Props helper for native <select> building pickers. */
export function getBuildingSelectProps(buildings: BuildingOption[]) {
  return {
    disabled: !needsBuildingSelection(buildings),
    showPlaceholder: needsBuildingSelection(buildings),
  };
}

/**
 * Apply default building selection without overriding an existing choice.
 * Falls back to the persisted app-wide selection (same across pages and refresh).
 */
export function applyDefaultBuildingSelection(
  buildings: BuildingOption[],
  user: UserWithBuilding | null | undefined,
  currentSelectedId: string,
): string {
  if (
    currentSelectedId &&
    buildings.some((building) => building.id === currentSelectedId)
  ) {
    return currentSelectedId;
  }

  const persisted = getPersistedBuildingId(user?.id);
  if (persisted && buildings.some((building) => building.id === persisted)) {
    return persisted;
  }

  return resolveDefaultBuildingId(buildings, user);
}
